import React from "react";
import BlogHero from "../blog/BlogHero";
import BlogQuote from "../blog/BlogQuote";
import BlogTip from "../blog/BlogTip";
import BlogImage from "../blog/BlogImage";
import BlogHeader from "../blog/BlogHeader";
import {
	ShoppingBag,
	MessageSquare,
	BarChart3,
	CalendarCheck,
	Gift,
} from "lucide-react";
import { Bot, Smartphone, Zap } from "lucide-react";

import heroImg from "../../assets/blog/digitalizar-hero.jpg";
import clientesImg from "../../assets/blog/digitalizar-clientes.jpg";
import BlogFooter from "../blog/BlogFooter";

export default function PostDigitalizarRestaurante() {
	return (
		<>
			<div className="font-poppins text-gray-800">
				{/* Header fijo del blog */}
				<BlogHeader />

				{/* Hero principal */}
				<BlogHero
					title="La digitalización de los restaurantes ya no es una opción, es una necesidad"
					subtitle="Cada vez más clientes esperan reservar mesa o pedir online desde su móvil. Descubre cómo adaptarte y crecer más rápido."
					image={heroImg}
				/>

				<main className="max-w-4xl mx-auto px-6 py-12 space-y-16">
					{/* Introducción */}
					<section>
						<p className="text-lg leading-relaxed mb-6">
							La forma en que los clientes se relacionan con los restaurantes ha
							cambiado por completo. Hoy esperan reservar mesa, pedir online o
							recibir promociones en su móvil y la experiencia digital se ha
							convertido en parte fundamental del sector.
						</p>

						<p className="text-lg leading-relaxed mb-6">
							En este contexto, digitalizar tu restaurante ya no es una opción,
							es un paso imprescindible para competir y diferenciarte. Los
							negocios que no lo hacen, corren el riesgo de perder clientes
							frente a alternativas más modernas y accesibles.
						</p>

						<BlogQuote text="La experiencia digital se ha convertido en un factor decisivo para elegir restaurante." />
					</section>

					{/* Bloque con imagen */}

					<BlogImage
						src={clientesImg}
						alt="Clientes pidiendo online"
						caption="Cada vez más usuarios prefieren pedir directamente desde el móvil o con un QR en mesa."
					/>

					{/* Ventajas */}
					<section>
						<h2 className="text-2xl font-bold mb-4">
							Principales ventajas de tener tu propia app
						</h2>
						<p className="text-lg leading-relaxed mb-6">
							Contar con una aplicación y una web propias no solo te ayuda a
							modernizar tu restaurante, sino que también te ofrece ventajas
							estratégicas frente a las plataformas de terceros:
						</p>
						<ul className="list-disc pl-6 space-y-3 text-lg">
							<li>
								<strong>Control total sobre tu marca:</strong> tu logo, tus
								colores, tu identidad. No apareces junto a cientos de
								competidores como en las apps de delivery.
							</li>
							<li>
								<strong>Pedidos sin comisiones:</strong> ahorra el 15-30% de
								comisión que cobran las grandes plataformas por cada pedido.
							</li>
							<li>
								<strong> Datos para fidelizar: </strong>entiende los gustos y
								costumbres de tus clientes y utiliza esos datos para enviar
								promociones personalizadas y relevantes.
							</li>
							<li>
								<strong>Reservas digitales:</strong> organiza tus mesas,
								horarios y disponibilidad sin llamadas de teléfono y con
								confirmaciones instantáneas.
							</li>
							<li>
								<strong>Fidelización:</strong> envía notificaciones push, crea
								cupones y programas de puntos para que los clientes siempre
								vuelvan.
							</li>
						</ul>
					</section>

					{/* Consejo destacado */}
					<BlogTip text="No intentes digitalizar todo de golpe. Empieza con un módulo (pedidos online, reservas, cupones) y ve escalando poco a poco. Tus clientes lo agradecerán y tu equipo se adaptará mejor." />

					{/* Casos de éxito */}
					<section>
						<h2 className="text-2xl font-bold mb-4">
							Restaurantes que ya lo están logrando
						</h2>
						<p className="text-lg leading-relaxed mb-6">
							Cada vez más negocios de hostelería están apostando por tener su
							propia app y web. El resultado es claro: menos dependencia de las
							grandes plataformas de delivery, una relación más directa con sus
							clientes y mayor recurrencia en los pedidos.
						</p>
						<ul className="space-y-4">
							<li className="flex items-center">
								<ShoppingBag className="w-6 h-6 text-[#43bd98] mr-3 flex-shrink-0" />
								<span>
									<strong>Pedidos directos más rentables:</strong> sin pagar el
									15–30 % de comisión a terceros.
								</span>
							</li>
							<li className="flex items-center">
								<MessageSquare className="w-6 h-6 text-[#43bd98] mr-3 flex-shrink-0" />
								<span>
									<strong>Comunicación directa:</strong> notificaciones push con
									promociones y menús especiales.
								</span>
							</li>
							<li className="flex items-center">
								<BarChart3 className="w-6 h-6 text-[#43bd98] mr-3 flex-shrink-0" />
								<span>
									<strong>Visión clara del negocio:</strong> estadísticas de
									ventas y hábitos de clientes para decidir mejor.
								</span>
							</li>
							<li className="flex items-center">
								<CalendarCheck className="w-6 h-6 text-[#43bd98] mr-3 flex-shrink-0" />
								<span>
									<strong>Experiencia del cliente optimizada:</strong> reservas
									online en segundos, sin llamadas ni esperas.
								</span>
							</li>
							<li className="flex items-center">
								<Gift className="w-6 h-6 text-[#43bd98] mr-3 flex-shrink-0" />
								<span>
									<strong>Más fidelización:</strong> cupones, tarjetas de puntos
									y descuentos exclusivos.
								</span>
							</li>
						</ul>
					</section>

					{/* Futuro */}
					<section className="bg-gray-50 rounded-2xl p-8 shadow-sm">
						<h2 className="text-2xl font-bold mb-6 text-gray-900">
							¿Qué esperar en los próximos años?
						</h2>

						<p className="text-lg leading-relaxed mb-6 text-gray-700">
							La digitalización no se detendrá aquí. El futuro de los
							restaurantes apunta hacia una evolución constante, marcada por la{" "}
							<strong>IA</strong>, la automatización y la personalización de la
							experiencia.
						</p>

						<ul className="space-y-4 text-lg text-gray-700">
							<li className="flex items-center">
								<Bot className="w-6 h-6 text-[#43bd98] mr-3 flex-shrink-0" />
								<span>
									<strong>Fidelización con IA:</strong> programas inteligentes
									que aprenden de los hábitos de consumo para enviar ofertas
									realmente relevantes.
								</span>
							</li>
							<li className="flex items-center">
								<Smartphone className="w-6 h-6 text-[#43bd98] mr-3 flex-shrink-0" />
								<span>
									<strong>Experiencias personalizadas:</strong> recomendaciones
									de platos y menús basados en el historial de pedidos de cada
									cliente.
								</span>
							</li>
							<li className="flex items-center">
								<Zap className="w-6 h-6 text-[#43bd98] mr-3 flex-shrink-0" />
								<span>
									<strong>Automatización total:</strong> reservas y gestión de
									mesas más eficientes, con menos carga operativa para el
									equipo.
								</span>
							</li>
						</ul>

						<p className="text-lg leading-relaxed mt-8 text-gray-700">
							Los restaurantes que se anticipen a estas tendencias no solo
							atraerán más clientes, sino que se consolidarán como referentes en
							su comunidad.
						</p>
					</section>

					{/* Conclusión */}
					<section className="bg-white border-t border-gray-100 pt-10">
						<h2 className="text-2xl font-bold mb-6 text-gray-900">
							Próximos pasos
						</h2>

						<p className="text-lg leading-relaxed mb-6 text-gray-700">
							Digitalizar tu restaurante no tiene por qué ser complicado. Con{" "}
							<strong>Applaby</strong> puedes lanzar tu app y tu web de pedidos
							en poco tiempo, sin conocimientos técnicos y con soporte
							personalizado en todo momento.
						</p>

						<p className="text-lg leading-relaxed mb-8 text-gray-700">
							La verdadera pregunta ya no es <em>si</em> dar el paso, sino{" "}
							<strong>cuándo</strong>. Y cuanto antes lo hagas, antes verás
							resultados: más clientes recurrentes, más ingresos y menos
							dependencia de terceros.
						</p>

						<div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
							<h3 className="text-xl font-semibold mb-3 text-gray-900">
								¿Listo para empezar?
							</h3>
							<p className="text-gray-600 mb-5">
								Solicita una demo gratuita y descubre cómo tu restaurante puede
								crecer gracias a la digitalización.
							</p>
							<a
								href="https://applaby.com/restaurantes/demo/"
								target="_blank"
								rel="noreferrer"
								className="inline-block bg-[#43bd98] text-white px-6 py-3 rounded-lg font-medium shadow hover:bg-[#37a885] transition"
							>
								👉 Solicitar demo gratis
							</a>
						</div>
					</section>
				</main>
			</div>
			<BlogFooter />
		</>
	);
}
